import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Lightbulb, Target, TrendingUp, ShieldCheck, Database, Cloud, Lock, Server } from 'lucide-react';
import { expertise } from '@/lib/data';

const AbstractInfographic = () => {
  return (
    <div className='relative w-full h-full min-h-[400px] bg-background/20 flex items-center justify-center overflow-hidden rounded-xl'>
      {/* Background Grid */}
      <div className='absolute inset-0 opacity-20' style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.2) 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
      
      {/* Central glow */}
      <div className='absolute inset-0 m-auto w-[60%] h-[60%] bg-primary/10 rounded-full blur-[80px]'></div>

      <div className='relative w-full max-w-[280px] aspect-square z-20'>
        {/* Connection Lines (Dash Animated) */}
        <svg className='absolute inset-0 w-full h-full z-10 pointer-events-none' style={{ filter: 'drop-shadow(0 0 4px rgba(0,255,255,0.3))'}}>
          <line x1='15%' y1='15%' x2='50%' y2='50%' stroke='currentColor' strokeWidth='2' strokeDasharray='4 4' className='text-primary/50 animate-pulse' />
          <line x1='85%' y1='15%' x2='50%' y2='50%' stroke='currentColor' strokeWidth='2' strokeDasharray='4 4' className='text-primary/50 animate-pulse' />
          <line x1='15%' y1='85%' x2='50%' y2='50%' stroke='currentColor' strokeWidth='2' strokeDasharray='4 4' className='text-primary/50 animate-pulse' />
          <line x1='85%' y1='85%' x2='50%' y2='50%' stroke='currentColor' strokeWidth='2' strokeDasharray='4 4' className='text-primary/50 animate-pulse' />
        </svg>

        {/* Center Node */}
        <div className='absolute inset-0 m-auto w-24 h-24 rounded-2xl bg-card border border-primary/40 flex items-center justify-center z-20 shadow-[0_0_30px_rgba(0,255,255,0.15)] animate-float-slow'>
          <Server className='w-10 h-10 text-primary drop-shadow-[0_0_10px_rgba(0,255,255,0.8)]' />
        </div>

        {/* Orbiting Nodes */}
        <div className='absolute top-0 left-0 w-16 h-16 rounded-xl bg-card border border-border flex items-center justify-center z-20 animate-float' style={{ animationDelay: '0.2s'}}>
          <Cloud className='w-6 h-6 text-foreground/80' />
        </div>
        <div className='absolute top-0 right-0 w-16 h-16 rounded-xl bg-card border border-border flex items-center justify-center z-20 animate-float' style={{ animationDelay: '0.4s'}}>
          <ShieldCheck className='w-6 h-6 text-accent drop-shadow-[0_0_8px_rgba(0,180,255,0.5)]' />
        </div>
        <div className='absolute bottom-0 left-0 w-16 h-16 rounded-xl bg-card border border-border flex items-center justify-center z-20 animate-float' style={{ animationDelay: '0.6s'}}>
          <Database className='w-6 h-6 text-primary' />
        </div>
        <div className='absolute bottom-0 right-0 w-16 h-16 rounded-xl bg-card border border-border flex items-center justify-center z-20 animate-float' style={{ animationDelay: '0.8s'}}>
          <Lock className='w-6 h-6 text-foreground/80' />
        </div>
      </div>

      {/* Decorative metrics overlay */}
      <div className='absolute bottom-6 left-6 right-6 flex justify-between px-5 py-3 bg-background/80 backdrop-blur-md border border-border/50 rounded-lg z-30 shadow-lg'>
          <div className='flex flex-col'>
             <span className='text-[10px] uppercase tracking-wider text-muted-foreground'>Uptime Status</span>
             <span className='text-sm font-mono font-bold text-primary'>99.999%</span>
          </div>
          <div className='flex flex-col text-right'>
             <span className='text-[10px] uppercase tracking-wider text-muted-foreground'>Network Health</span>
             <span className='text-sm font-mono font-bold text-accent'>OPTIMAL</span>
          </div>
      </div>
    </div>
  );
}

const AboutSection = () => {
  const values = [
    {
      icon: <Lightbulb className='w-6 h-6' />,
      title: 'Resilience',
      description:
        'Architecting fault-tolerant systems built for high availability and scale'
    },
    {
      icon: <Users className='w-6 h-6' />,
      title: 'Leadership',
      description: 'Building and scaling high-performing remote engineering teams'
    },
    {
      icon: <Target className='w-6 h-6' />,
      title: 'Compliance-First',
      description: 'Seamlessly weaving risk management and regulatory requirements into core infrastructure'
    },
    {
      icon: <TrendingUp className='w-6 h-6' />,
      title: 'Performance',
      description:
        'Optimizing system throughput while maintaining robust data integrity'
    }
  ];

  return (
    <section id='about' className='py-6 lg:py-32 relative'>
      <div className='container mx-auto px-4 lg:px-8 relative z-10'>
        <div className='text-center mb-16'>
          <Badge variant='outline' className='mb-4'>
            About Me
          </Badge>
          <h2 className='text-3xl lg:text-5xl font-bold mb-6'>
            Architecting <span className='text-primary'>Scale</span> & Stability
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto'>
            With over 8 years of experience in FinTech leadership, I help
            organizations scale complex financial ecosystems and achieve
            technological maturity through robust infrastructure design.
          </p>
        </div>

        <div className='grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20'>
          {/* Infographic */}
          <div className='animate-fade-in h-full'>
            <Card className='card-elegant overflow-hidden h-full p-2 border-border/30 bg-card/30'>
              <AbstractInfographic />
            </Card>
          </div>

          {/* Content */}
          <div className='animate-fade-in' style={{ animationDelay: '0.2s' }}>
            <h3 className='text-2xl lg:text-3xl font-bold mb-6'>
              Transforming Financial Platforms Through Strategic Architecture
            </h3>
            <p className='text-muted-foreground mb-6 leading-relaxed'>
              As a seasoned FinTech CTO and Architect, I specialize in
              bridging the gap between complex regulatory requirements and scalable
              infrastructure. My approach combines deep technical expertise in microservices
              with a resilient event-driven mentality to deliver solutions that drive real
              value.
            </p>
            <p className='text-muted-foreground mb-8 leading-relaxed'>
              From scaling digital wallets to global payments integration, I've helped organizations
              build distributed systems capable of handling high throughput securely. My leadership
              ensures engineering teams adopt modern architecture paradigms seamlessly.
            </p>

            {/* Expertise Tags */}
            <div className='mb-8'>
              <h4 className='text-lg font-semibold mb-4'>Core Expertise</h4>
              <div className='flex flex-wrap gap-3'>
                {expertise.map((skill, index) => (
                  <Badge
                    key={skill}
                    variant='secondary'
                    className='animate-fade-in-scale'
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Values */}
        <div>
          <h3 className='text-2xl lg:text-3xl font-bold text-center mb-12'>
            My <span className='text-primary'>Core Values</span>
          </h3>
          <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-6'>
            {values.map((value, index) => (
              <Card
                key={value.title}
                className='card-elegant p-6 text-center group hover:glow-effect transition-all duration-300 animate-fade-in-scale'
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className='p-0'>
                  <div className='mb-4 flex justify-center'>
                    <div className='p-3 bg-primary/10 rounded-lg text-primary group-hover:scale-110 transition-transform duration-300'>
                      {value.icon}
                    </div>
                  </div>
                  <h4 className='text-lg font-semibold mb-2'>{value.title}</h4>
                  <p className='text-sm text-muted-foreground'>
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
